import { captureException } from '@/services/TrackerService/Sentry';
import { checkIsBrowser } from '@/utils/functions/checkServerEnvironment';
import { getYaNative } from '@yanolja-rnd/ya-native';
import ReactGA4 from 'react-ga4';
import type { UaEventOptions } from 'react-ga4/types/ga4';

interface IGA4TrackerProps {
  gaApiKey: string;
}

// 초기화 이전 전송 호출시 로그를 담을 배열
const MAX_BEFORE_INITIALIZE_TEMPORAL_LOG = 30;
const _beforeInitializeEventLog: { optionsOrName: UaEventOptions | string; data?: { [key: string]: any } }[] = [];
const _beforeInitializePageViewLog: { page: string; title: string }[] = [];
let _gaApiKey: string;

/**
 * GA4 클라이언트 로그를 전송하는 객체
 */
const _GA4Tracker = {
  /**
   * 웹 GA4 통계 전송을 위해 설치
   * @param gaApiKey {string} ga4 api 키
   */
  initialize({ gaApiKey }: IGA4TrackerProps): void {
    try {
      if (!checkIsBrowser()) return;

      _gaApiKey = gaApiKey;

      // 웹에서만 ReactGa4 설치
      if (!getYaNative().isEnabled && gaApiKey) {
        ReactGA4.initialize(gaApiKey);
      }

      // 초기화 이전 담았던 로그 제거
      _beforeInitializeEventLog.forEach(({ optionsOrName, data }) => this.event(optionsOrName, data));
      _beforeInitializeEventLog.splice(0);
      _beforeInitializePageViewLog.forEach((log) => this.event(log));
      _beforeInitializePageViewLog.splice(0);
    } catch (e) {
      captureException(e);
    }
  },

  /**
   * GA4 커스텀 이벤트 전송
   * @param optionsOrName
   * @param data
   */
  event(optionsOrName: UaEventOptions | string, data?: { [key: string]: any }): void {
    try {
      if (!_gaApiKey) {
        _beforeInitializeEventLog.push({ optionsOrName, data });
        if (_beforeInitializeEventLog.length > MAX_BEFORE_INITIALIZE_TEMPORAL_LOG) {
          _beforeInitializeEventLog.shift();
        }
        return;
      }

      // GA4 웹/앱 분기처리 전송
      const _yaNative = getYaNative();
      if (!_yaNative.isEnabled) {
        ReactGA4.event(optionsOrName, data);
      } else if (typeof optionsOrName === 'string') {
        _yaNative.ga4f({ optionsOrName, ...data }, () => null);
      } else {
        _yaNative.ga4f({ ...optionsOrName, ...data }, () => null);
      }
    } catch (e) {
      captureException(e);
    }
  },

  /**
   * pageview 전송
   *
   * @param page page 경로
   * @param title page 타이틀
   */
  pageView({ page, title }: { page: string; title: string }): void {
    try {
      if (!_gaApiKey) {
        _beforeInitializePageViewLog.push({ page, title });
        if (_beforeInitializePageViewLog.length > MAX_BEFORE_INITIALIZE_TEMPORAL_LOG) {
          _beforeInitializePageViewLog.shift();
        }
        return;
      }
      ReactGA4.send({ hitType: 'pageview', page, title });
    } catch (e) {
      captureException(e);
    }
  },
};

export default _GA4Tracker;
